import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import SyntheticBanner from "./SyntheticBanner";
import {
  Boxes, CalendarDays, CircleDollarSign, ClipboardCheck, ClipboardList, Clock3,
  Factory, FilePlus2, FolderOpen, LayoutDashboard, Layers3, LogOut,
  PackageCheck, PanelLeftClose, PanelLeftOpen, Settings, UserPlus, Users,
} from "lucide-react";

// V1's exact main navigation. Geometry Jobs and Files remain available as
// diagnostic routes, but do not belong in the quoting navigation.
const groups = [
  { title: "Overview", items: [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard }] },
  { title: "Quotes", items: [
    { to: "/quotes/new", label: "New Quote", icon: FilePlus2 },
    { to: "/quotes", label: "All Quotes", icon: FolderOpen, end: true },
    { to: "/quotes/expired", label: "Expired", icon: Clock3 },
    { to: "/quotes/near-expiry", label: "Quotes Near Expiry", icon: Clock3 },
    { to: "/quotes/draft", label: "Draft", icon: ClipboardList },
    { to: "/quotes/pending-approval", label: "Pending Approval", icon: ClipboardCheck },
  ] },
  { title: "Customers", items: [
    { to: "/customers", label: "Manage Customers", icon: Users, end: true },
    { to: "/customers/new", label: "New Customer", icon: UserPlus },
  ] },
  { title: "Orders", items: [
    { to: "/orders", label: "View All Orders", icon: Boxes, end: true },
    { to: "/orders/nesting", label: "Nesting Orders", icon: Layers3 },
    { to: "/orders/schedule", label: "Smart Schedule", icon: CalendarDays },
    { to: "/orders/production", label: "Production Board", icon: Factory },
    { to: "/orders/material-requirements", label: "Material Requirements", icon: PackageCheck },
  ] },
  { title: "Materials", items: [
    { to: "/materials", label: "Materials", icon: Layers3 },
    { to: "/material-costs", label: "Material Costs", icon: CircleDollarSign },
  ] },
];

export default function AppShell() {
  const { session, logout } = useAuth();
  const navigate = useNavigate();
  const [branding, setBranding] = useState(null);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sq-sidebar-collapsed") === "1");
  useEffect(() => {
    let active = true;
    const load = () => api.get("/document-settings/app-branding").then(({ data }) => { if (active) setBranding(data); }).catch(() => {});
    load(); window.addEventListener("sq-branding-saved", load);
    return () => { active = false; window.removeEventListener("sq-branding-saved", load); };
  }, [session?.user?.id]);
  function toggleSidebar() { setCollapsed((value) => { localStorage.setItem("sq-sidebar-collapsed", value ? "0" : "1"); return !value; }); }
  const doLogout = () => { logout(); navigate("/login"); };
  const visibleGroups = session?.user?.role === "admin" ? [...groups, { title: "System", items: [{ to: "/setup", label: "Company Setup", icon: ClipboardCheck },{ to: "/settings", label: "Settings", icon: Settings }] }] : groups;

  return <div className="min-h-screen flex flex-col bg-[var(--sq-page)]">
    <SyntheticBanner organisation={session?.organisation} />
    <div className="flex flex-1 min-h-0">
      <aside className={`sq-main-sidebar shrink-0 text-slate-200 flex flex-col transition-[width] duration-200 ${collapsed ? "w-[72px]" : "w-[238px]"}`} style={{ "--sq-nav-accent": branding?.accent_colour || "#dc2638" }}>
        <button type="button" aria-label={collapsed ? "Expand menu" : "Collapse menu"} title={collapsed ? "Expand menu" : "Collapse menu"} onClick={toggleSidebar} className="absolute -right-3 top-7 z-20 grid h-7 w-7 place-items-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50">{collapsed ? <PanelLeftOpen className="h-3.5 w-3.5" /> : <PanelLeftClose className="h-3.5 w-3.5" />}</button>
        <div className={`relative border-b border-white/10 bg-black/20 ${collapsed ? "px-2 py-4" : "px-5 py-5"}`}>
          {branding?.app_logo_data_uri ? <img src={branding.app_logo_data_uri} alt={branding.trading_name || branding.company_name || "Company logo"} className={`mx-auto w-full object-contain ${collapsed ? "h-9" : "max-h-16"}`} /> : collapsed ? <div className="mx-auto grid h-9 w-9 place-items-center rounded-md bg-white/10 text-sm font-black text-white">SQ</div> : <div className="font-display text-xl font-bold text-white leading-tight">{branding?.trading_name || branding?.company_name || "SmartQuote"}</div>}
          {!collapsed && <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400 mt-2">SmartQuote V2</div>}
        </div>
        <nav className={`relative flex-1 overflow-y-auto py-4 ${collapsed ? "px-1" : "px-2"}`}>
          {visibleGroups.map((group) => <div key={group.title} className="mb-4">{!collapsed && <div className="mb-1.5 px-2 text-[9px] font-bold uppercase tracking-[0.2em] text-slate-500">{group.title}</div>}<div className="space-y-0.5">{group.items.map((item) => <NavItem key={item.to} {...item} collapsed={collapsed} />)}</div></div>)}
        </nav>
        <div className={`relative border-t border-white/10 bg-black/20 ${collapsed ? "p-2" : "px-4 py-3"}`}>
          {!collapsed && <><div className="text-[10px] uppercase tracking-wider text-slate-500">Signed in</div><div data-testid="app-user-email" className="text-sm text-white truncate mt-0.5">{session?.user?.email}</div><div className="text-[11px] capitalize text-slate-400 mt-0.5">{session?.user?.role}</div></>}
          <button data-testid="logout-button" onClick={doLogout} title="Sign out" className={`${collapsed ? "mt-0 h-10 w-full" : "mt-3 w-full px-2 py-1.5"} inline-flex items-center justify-center gap-2 rounded border border-slate-700 text-xs text-slate-200 hover:bg-white/10`}><LogOut className="w-3.5 h-3.5" />{!collapsed && "Sign out"}</button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 overflow-y-auto"><Outlet /></main>
    </div>
  </div>;
}

function NavItem({ to, label, icon: Icon, end = false, collapsed }) {
  return <NavLink title={collapsed ? label : undefined} to={to} end={end} data-testid={`nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className={({ isActive }) => `sq-main-nav-item ${collapsed ? "justify-center px-2" : ""} ${isActive ? "is-active" : ""}`}><Icon className="h-4 w-4 shrink-0" />{!collapsed && <span className="truncate">{label}</span>}</NavLink>;
}
